import { useEffect, useState } from "react";

import { TextField, Select, Button, DatePicker, Typography, 
  InfoPanel, Checkbox } from "@king-ict/eupisi-ui/components";
import { IconWrapper } from "@king-ict/eupisi-ui/components";
import { ReactComponent as ArrowRight } from "@king-ict/eupisi-ui/components/Icon/ArrowRight.svg";
import { ReactComponent as Info } from "@king-ict/eupisi-ui/components/Icon/Info.svg";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";


import {
  getSchoolCandidateById, getSchoolCandidateOib, saveSchoolCandidate
} from "../../api/SchoolCandidate/SchoolCandidateApi";
import { useTypedSelector } from "../../app/rootReducer";
import { useAppDispatch } from "../../app/store";
import { ContentArea, ToolbarTop, ToolbarEdit, FormCard, AddressSelect, CountySelect, InstitutionSelect } from "../../components";
import { useAuth, useAuthCounties, useDidUpdateEffect } from "../../hooks";
import {
  OibServiceResult, ValidationFailure, ServiceResultStatus, Address, GenderEnum, Role,
} from "../../models";
import { PersonAddress } from "../../models/Request/PersonAddress";
import { getDefaultSchoolCandidate, SchoolCandidate, SchoolCandidateStatus } from "../../models/SchoolCandidate";
import { ClaimType } from "../../models/User";
import {
  useGetAlternativeCodeReasonListQuery, useGetAlternativeCodeTypeListQuery,
  useGetCountyListForUserQuery,
  useGetGenderListQuery, useGetInstitutionListForUserQuery, useGetSchoolCandidateAddReasonListQuery,
  useGetSchoolCandidateCancelReasonListQuery,
  useGetSchoolYearCurrentQuery
} from "../../queries/codeBookQuery";
import { schoolCandidateDetailUrl, schoolCandidateRoute } from "../../routes/routes";
import { resetIsEdited, resetRedirectURL, setIsEdited, setSaveOK } from "../../slices/applicationSlice";
import { FormatUtils, MsgUtils } from "../../utils";

export function SchoolCandidateEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const { data: currentSchoolYear } = useGetSchoolYearCurrentQuery();
  const { data: authCounties } = useGetCountyListForUserQuery();
  const { data: institutions } = useGetInstitutionListForUserQuery();
  //const authCounties = useAuthCounties();
  const oibRegex = /^\d{11}$/;

  const defaultSchoolCandidate = getDefaultSchoolCandidate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [schoolCandidate, setSchoolCandidate] = useState<SchoolCandidate>(defaultSchoolCandidate);

  const [showOibAlert, setShowOibAlert] = useState<string | undefined>();
  const [showOibSuccess, setShowOibSuccess] = useState<string | undefined>();

  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [oibServiceResult, setOibServiceResult] = useState<OibServiceResult<SchoolCandidate>>();

  const { data: genders } = useGetGenderListQuery();
  const [statuses] = useState<CodeBook[]>(MsgUtils.getEnumToCodeBook(SchoolCandidateStatus, "schoolCandidateStatus"));
  const { data: schoolCandidateAlternativeCodeReasons } = useGetAlternativeCodeReasonListQuery();
  const { data: schoolCandidateAlternativeCodeTypes } = useGetAlternativeCodeTypeListQuery();
  const { data: schoolCandidateAddReasons } = useGetSchoolCandidateAddReasonListQuery();
  const { data: schoolCandidateCancelReasons } = useGetSchoolCandidateCancelReasonListQuery();

  const redirectURL = useTypedSelector((state) => state.application.redirectURL);

  useDidUpdateEffect(() => {
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate
    };
    if (currentSchoolYear) {
      newSchoolCandidate.schoolYearId = currentSchoolYear.id;
    }
    if (authCounties && authCounties.length > 0) {
      newSchoolCandidate.uoguCountyId = authCounties[0].id;
    }
    if (auth && auth.currentRole?.claimType === ClaimType.Institution
      && institutions && institutions.length > 0) {
      newSchoolCandidate.institutionId = institutions[0].id;
    }
    setSchoolCandidate(newSchoolCandidate);
  }, [currentSchoolYear, auth, authCounties, institutions]);

  useEffect(() => {
    (async () => {
      console.log("id", id)
      if (id !== "0") {
        var result = (await getSchoolCandidateById(+id));
        if (result) {

          setSchoolCandidate(() => result);
          if (!result.isOibConnected && result.oib && oibRegex.test(result.oib) && id !== "0") // existing, not OIB connected
            updateOibSystemData(result.oib, true, true);
          console.log("status", statuses.find(v => v.id === schoolCandidate?.statusId)?.name);
        }
      }
      setIsLoading(false);
    })();
    dispatch(resetIsEdited())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handlePermanentAddressChange = (newAddress: Address) => {
    console.table(newAddress)
    if (newAddress) {
      const newResidence: PersonAddress = {
        ...schoolCandidate?.residence ?? { id: 0 },
        permanentAddress: newAddress,
      };
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        residence: newResidence,
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    dispatch(setIsEdited());
  }
  const handleTemporaryAddressChange = (newAddress: Address) => {
    console.table(newAddress)
    if (newAddress) {
      const newResidence: PersonAddress = {
        ...schoolCandidate?.residence ?? { id: 0 },
        temporaryAddress: newAddress,
      };
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        residence: newResidence,
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    dispatch(setIsEdited());
  }
  const handleOibChanged = (e: any) => {
    const oib = e.target.value as string;
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate,
      oib: oib && oib.length > 0 ? oib : undefined
    };
    setSchoolCandidate(newSchoolCandidate);
    dispatch(setIsEdited());
    setShowOibAlert(undefined);
    setShowOibSuccess(undefined);
  };
  const handleSchoolCandidateAlternativeCodeChange = (e: any) => {
    const alternativeCode = e.target.value as string;
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate,
      alternativeCode: alternativeCode
    };
    setSchoolCandidate(newSchoolCandidate);
    dispatch(setIsEdited());
  };
  const handleLastNameChanged = (e: any) => {
    const lastName = e.target.value as string;
    handleLastNameChangedString(lastName);
  };
  const handleLastNameChangedString = (t: string | undefined) => {
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate,
      lastName: t ?? ""
    };
    setSchoolCandidate(newSchoolCandidate);
    dispatch(setIsEdited());
  };
  const handleFirstNameChanged = (e: any) => {
    const firstName = e.target.value as string;
    handleFirstNameChangedString(firstName);
  };
  const handleFirstNameChangedString = (t: string | undefined) => {
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate,
      firstName: t ?? ""
    };
    setSchoolCandidate(newSchoolCandidate);
    dispatch(setIsEdited());
  };
  const handleIsHandicappedChanged = () => {
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate,
      isHandicapped: !schoolCandidate.isHandicapped,
    };
    setSchoolCandidate(newSchoolCandidate);
    dispatch(setIsEdited());
  };
  const handleGenderChange = (e: any) => {
    const genderId = Number.parseInt(e.target.value);
    handleGenderChangeNumber(genderId);
  };
  const handleGenderChangeNumber = (i: number | undefined) => {
    if (i && !isNaN(i)) {
      //var schoolCandidateGender: CodeBook = { id: i, name: "" };
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        genderId: i,
      };
      setSchoolCandidate(newSchoolCandidate);
      dispatch(setIsEdited());
    }
  };
  const handleDateOfBirthChange = (dateOfBirth: Date) => {
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate,
      dateOfBirth: dateOfBirth
    };
    setSchoolCandidate(newSchoolCandidate);
    dispatch(setIsEdited());
  };
  // Leave this until we can change to the real DatePicker.
  const handleDateOfBirthChangeAny = (e: any) => {
    var dateOfBirth = new Date(e.target.value);
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate,
      dateOfBirth: dateOfBirth
    };
    setSchoolCandidate(newSchoolCandidate);
    dispatch(setIsEdited());
  };
  const handleFatherNameChanged = (e: any) => {
    const fatherName = e.target.value as string;
    handleFatherNameChangedString(fatherName);
  };
  const handleFatherNameChangedString = (t: string | undefined) => {
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate,
      fatherName: t
    };
    setSchoolCandidate(newSchoolCandidate);
    dispatch(setIsEdited());
  };
  const handleMotherNameChanged = (e: any) => {
    const motherName = e.target.value as string;
    handleMotherNameChangedString(motherName);
  };
  const handleMotherNameChangedString = (t: string | undefined) => {
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate,
      motherName: t
    };
    setSchoolCandidate(newSchoolCandidate);
    dispatch(setIsEdited());
  };
  const handleSchoolCandidateAlternativeCodeTypeChange = (e: any) => {
    const schoolCandidateAlternativeCodeTypeId = Number.parseInt(e.target.value);
    if (!isNaN(schoolCandidateAlternativeCodeTypeId)) {
      var schoolCandidateAlternativeCodeType: CodeBook = { id: schoolCandidateAlternativeCodeTypeId, name: "" };
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        schoolCandidateAlternativeCodeTypeId: schoolCandidateAlternativeCodeTypeId,
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    else {
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        schoolCandidateAlternativeCodeTypeId: undefined
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    dispatch(setIsEdited());
  };
  const handleSchoolCandidateAlternativeCodeReasonChange = (e: any) => {
    const schoolCandidateAlternativeCodeReasonId = Number.parseInt(e.target.value);
    if (!isNaN(schoolCandidateAlternativeCodeReasonId)) {
      var schoolCandidateAlternativeCodeReason: CodeBook = { id: schoolCandidateAlternativeCodeReasonId, name: "" };
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        schoolCandidateAlternativeCodeReasonId: schoolCandidateAlternativeCodeReasonId
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    else {
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        schoolCandidateAlternativeCodeReasonId: undefined
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    dispatch(setIsEdited());
  };
  const handleSchoolCandidateAddReasonChange = (e: any) => {
    const schoolCandidateAddReasonId = Number.parseInt(e.target.value);
    if (!isNaN(schoolCandidateAddReasonId)) {
      var schoolCandidateAddReason: CodeBook = { id: schoolCandidateAddReasonId, name: "" };
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        schoolCandidateAddReasonId: schoolCandidateAddReasonId
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    else {
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        schoolCandidateAddReasonId: undefined
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    dispatch(setIsEdited());
  };
  const handleSchoolCandidateAddReasonOtherChange = (e: any) => {
    const schoolCandidateAddReasonOther = e.target.value as string;
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate,
      schoolCandidateAddReasonOther: schoolCandidateAddReasonOther && schoolCandidateAddReasonOther.length > 0 ? schoolCandidateAddReasonOther : undefined,
    };
    setSchoolCandidate(newSchoolCandidate);
    dispatch(setIsEdited());
  };
  const handleSchoolCandidateCancelReasonChange = (e: any) => {
    const schoolCandidateCancelReasonId = Number.parseInt(e.target.value);
    if (!isNaN(schoolCandidateCancelReasonId)) {
      var schoolCandidateCancelReason: CodeBook = { id: schoolCandidateCancelReasonId, name: "" };
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        schoolCandidateCancelReasonId: schoolCandidateCancelReasonId
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    else {
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        schoolCandidateCancelReasonId: undefined
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    dispatch(setIsEdited());
  };
  const handleSchoolCandidateCancelReasonOtherChange = (e: any) => {
    const schoolCandidateCancelReasonOther = e.target.value as string;
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate,
      schoolCandidateCancelReasonOther: schoolCandidateCancelReasonOther && schoolCandidateCancelReasonOther.length > 0 ? schoolCandidateCancelReasonOther : undefined,
    };
    setSchoolCandidate(newSchoolCandidate);
    dispatch(setIsEdited());
  };
  const handlePermanentAddressNoteChanged = (e: any) => {
    const permanentAddressNote = e.target.value as string;
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate,
      permanentAddressNote: permanentAddressNote
    };
    setSchoolCandidate(newSchoolCandidate);
    dispatch(setIsEdited());
  };
  const handleUOGUNoteChange = (e: any) => {
    const val = e.target.value as string;
    const newSchoolCandidate: SchoolCandidate = {
      ...schoolCandidate,
      uoguNote: val,
    };
    setSchoolCandidate(newSchoolCandidate);
    dispatch(setIsEdited());
  };
  const handleConnectDisconnectOibSystem = async () => {
    if (!schoolCandidate.isOibConnected && schoolCandidate.oib) {
      const forUpdate = id !== "0";
      updateOibSystemData(schoolCandidate.oib, false, forUpdate);
    } else {
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        isOibConnected: false,
      };
      setSchoolCandidate(newSchoolCandidate);
      dispatch(setIsEdited());
    }
    setOibServiceResult(undefined);
  };
  const updateOibSystemData = async (oib: string, forStorage: boolean, forUpdate: boolean) => {
    if (oibRegex.test(oib)) {
      var result = (await getSchoolCandidateOib(oib, forUpdate));
      if (result 
          && (result.status & ServiceResultStatus.Success) === ServiceResultStatus.Success 
          && result.item && result.item.oib) {
        if (forStorage) {
          setOibServiceResult(result);
          setShowOibSuccess(MsgUtils.getOibErrorMessage(result.status));
        } else {
          const uoguCountyId = schoolCandidate.uoguCountyId !== 0 ? schoolCandidate.uoguCountyId : (result.item.residence?.temporaryAddress?.countyId ?? result.item.residence?.permanentAddress?.countyId);

          const newSchoolCandidate: SchoolCandidate = {
            ...schoolCandidate,
            isOibConnected: result.status === ServiceResultStatus.Success,
            firstName: result.item.firstName,
            lastName: result.item.lastName,
            dateOfBirth: result.item.dateOfBirth,
            genderId: result.item.genderId,
            motherName: (result.item.motherName && result.item.motherName !== "") ? result.item.motherName : schoolCandidate.motherName,
            fatherName: result.item.fatherName,
            residence: result.item.residence,
            uoguCountyId: uoguCountyId ?? 0,

            alternativeCode: undefined,
            schoolCandidateAlternativeCodeReasonId: undefined,
            schoolCandidateAlternativeCodeTypeId: undefined,
          };
          setSchoolCandidate(newSchoolCandidate);
          dispatch(setIsEdited());
          setShowOibSuccess(MsgUtils.getOibErrorMessage(result.status));
        }
      } else {
        setOibServiceResult(undefined);
        if (result.status === ServiceResultStatus.OibExistsInSchoolCandidate && result.item?.uoguCountyId)
          setShowOibAlert(t(MsgUtils.getOibErrorMessage(result.status), { countyName: authCounties?.find(v => v.id === result?.item?.uoguCountyId)?.name }));
        else
          setShowOibAlert(t(MsgUtils.getOibErrorMessage(result.status)));
      }
    }
  };
  const handleUOGUCountyChange = (e: any) => {
    const countyId = Number.parseInt(e.target.value);
    if (!isNaN(countyId)) {
      var ministryCounty: CodeBook = { id: countyId, name: "" };
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        //uoguCounty: ministryCounty,
        uoguCountyId: countyId,
        institutionId: undefined
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    // else {
    //   const newSchoolCandidate: SchoolCandidate = {
    //     ...schoolCandidate,
    //     uoguCounty: undefined,
    //     institution: undefined
    //   };
    //   setSchoolCandidate(newSchoolCandidate);
    // }
    dispatch(setIsEdited());
  };
  const handleInstitutionChange = (e: any) => {
    const institutionId = Number.parseInt(e.target.value);
    if (!isNaN(institutionId)) {
      var institution: CodeBook = { id: institutionId, name: "" };
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        institutionId: institutionId,
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    else {
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        institutionId: undefined,
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    dispatch(setIsEdited());
  };
  const renderOibServiceInfo = (showButton: boolean, colWidth: number, message?: string, clickHandler?: () => void, buttonDisabled?: boolean) => {
    const effectiveColWidth = showButton ? colWidth - 1 : colWidth;
    const effectiveDisabled = buttonDisabled ?? false;
    return (
      <>
        {showButton &&
          <Col md={1}>
            <Button
              outline={true}
              size="sm"
              onClick={clickHandler}
              disabled={effectiveDisabled}
            >
              <IconWrapper color="primary">
                <ArrowRight />
              </IconWrapper>
            </Button>
          </Col>
        }
        <Col md={effectiveColWidth}>
          <Typography color="primary">
            {message}
          </Typography>
        </Col>
      </>
    )
  };
  const handleSaveSchoolCandidate = () => {
    saveSchoolCandidate(schoolCandidate).then((result) => {
      if (result && result.result !== undefined) {
        if (result.isValid) {
          dispatch(resetIsEdited())
          dispatch(setSaveOK())
          if (authCounties?.find(x => x.id === schoolCandidate?.uoguCountyId))
            history.replace(schoolCandidateDetailUrl(result.result?.id))
          else {
            if (redirectURL) {
              dispatch(resetRedirectURL())
              history.push(redirectURL);
            }
            else {
              history.replace(schoolCandidateRoute);
            }
          }

        }
        setErrors(result.validationFailures);
      }
    });
  };

  if (isLoading) return null;
  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={"hide"}
        pageTitle={t("navigation.schoolCandidateList")}
        errors={errors}
      />
      <FormCard
        cardTitle={t("common.schoolCandidateModel.schoolCandidatePersonalDetails")}
        labelStatus={
          {
            color: FormatUtils.getSchoolCandidateStatusColor(schoolCandidate.statusId)
            , name: statuses.find(v => v.id === schoolCandidate.statusId)?.name ?? ""
          }
        }
      >
        <Row>
          <Col md={3}>
            <TextField
              label={t("common.schoolCandidateModel.oib")}
              value={schoolCandidate.oib}
              readonly={schoolCandidate.isOibConnected}
              onChange={handleOibChanged}
              plaintext={schoolCandidate.isOibConnected}
              errorText={errors?.find(e => e.propertyName === "Person.OIB")?.message}
            />
          </Col>
          <Col md={4}>
            <br />
            <Button onClick={handleConnectDisconnectOibSystem}
              disabled={!(schoolCandidate.oib !== undefined && oibRegex.test(schoolCandidate.oib))}
              size="sm"
            >
              {schoolCandidate.isOibConnected ? t("common.schoolCandidateModel.disconnectOibSystem") : t("common.schoolCandidateModel.connectOibSystem")}
            </Button>
            {(schoolCandidate.isOibConnected && id !== "0") &&
              <Button onClick={() => updateOibSystemData(schoolCandidate.oib!, false, true)}
                disabled={!(schoolCandidate.oib !== undefined && oibRegex.test(schoolCandidate.oib))}
                outline={true}
                size="sm"
              >
                {t("common.schoolCandidateModel.refreshOibData")}
              </Button>
            }
          </Col>
          <Col md={5}>
            {!showOibAlert || <InfoPanel color="danger" message="" dismissible={false} fullWidth={true} >{showOibAlert}</InfoPanel>}
            {!showOibSuccess || <InfoPanel color="success" message="" dismissible={false} fullWidth={true} >{showOibSuccess}</InfoPanel>}
          </Col>

          {(!schoolCandidate.isOibConnected && id !== "0" && oibServiceResult) &&
            <Col md={4}>
              <IconWrapper color="primary">
                <Info />
              </IconWrapper>
              <Typography color="primary">
                {t("common.schoolCandidateModel.disconnectedOibSystem")}
              </Typography>
            </Col>
          }
        </Row>
        {!(schoolCandidate.oib !== undefined && oibRegex.test(schoolCandidate.oib)) &&
          <Row>
            <Col md={4}>
              <TextField
                label={t("common.schoolCandidateModel.alternativeCode")}
                value={schoolCandidate.alternativeCode}
                readonly={schoolCandidate.isOibConnected}
                onChange={handleSchoolCandidateAlternativeCodeChange}
                plaintext={schoolCandidate.isOibConnected}
                errorText={errors?.find(e => e.propertyName === "Person.AlternativeDocumentNumber")?.message}
              />
            </Col>
            <Col md={4}>
              <Select
                label={t("common.schoolCandidateModel.schoolCandidateAlternativeCodeType")}
                name={"schoolCandidate.schoolCandidateAlternativeCodeType"}
                value={schoolCandidate.schoolCandidateAlternativeCodeTypeId}
                onChange={handleSchoolCandidateAlternativeCodeTypeChange}
                selectData={codeBookToSelectData(schoolCandidateAlternativeCodeTypes)}
                readonly={schoolCandidate.isOibConnected}
                emptyOption={{ value: undefined, text: undefined }}
                borderless={schoolCandidate.isOibConnected}
                errorText={errors?.find(e => e.propertyName === "Person.AlternativeDocumentTypeId")?.message}
              />
            </Col>
            <Col md={4}>
              <Select
                label={t("common.schoolCandidateModel.schoolCandidateAlternativeCodeReason")}
                name={"schoolCandidate.schoolCandidateAlternativeCodeReason"}
                value={schoolCandidate.schoolCandidateAlternativeCodeReasonId}
                onChange={handleSchoolCandidateAlternativeCodeReasonChange}
                selectData={codeBookToSelectData(schoolCandidateAlternativeCodeReasons)}
                readonly={schoolCandidate.isOibConnected}
                emptyOption={{ value: undefined, text: undefined }}
                borderless={schoolCandidate.isOibConnected}
                errorText={errors?.find(e => e.propertyName === "Person.AlternativeDocumentReasonId")?.message}
              />
            </Col>
          </Row>
        }
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.lastName")}
              value={schoolCandidate.lastName}
              readonly={schoolCandidate.isOibConnected}
              onChange={handleLastNameChanged}
              plaintext={schoolCandidate.isOibConnected}
              errorText={errors?.find(e => e.propertyName === "LastName")?.message}
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.firstName")}
              value={schoolCandidate.firstName}
              readonly={schoolCandidate.isOibConnected}
              onChange={handleFirstNameChanged}
              plaintext={schoolCandidate.isOibConnected}
              errorText={errors?.find(e => e.propertyName === "FirstName")?.message}
            />
          </Col>
        </Row>
        {oibServiceResult &&
          <Row>
            {renderOibServiceInfo(true, 6, oibServiceResult.item?.lastName, () => handleLastNameChangedString(oibServiceResult.item?.lastName))}
            {renderOibServiceInfo(true, 6, oibServiceResult.item?.firstName, () => handleFirstNameChangedString(oibServiceResult.item?.firstName))}
          </Row>
        }
        <Row>
          <Col md={3}>
            <DatePicker
              label={t("common.schoolCandidateModel.dateOfBirth")}
              name={"schoolCandidate.dateOfBirth"}
              onChange={handleDateOfBirthChangeAny}
              disabled={schoolCandidate.isOibConnected}
              value={schoolCandidate.dateOfBirth}
              errorText={errors?.find(e => e.propertyName === "DateOfBirth")?.message}
            />
          </Col>
          <Col md={3}>
            <Select
              label={t("common.schoolCandidateModel.schoolCandidateGender")}
              name={"schoolCandidate.gender"}
              value={schoolCandidate.genderId}
              onChange={handleGenderChange}
              selectData={codeBookToSelectData(genders)}
              disabled={schoolCandidate.isOibConnected}
              borderless={schoolCandidate.isOibConnected}
              errorText={errors?.find(e => e.propertyName === "GenderId")?.message}
            />
          </Col>
          <Col md={3}>
            <TextField
              label={t("common.schoolCandidateModel.fatherName")}
              value={schoolCandidate.fatherName}
              onChange={handleFatherNameChanged}
              errorText={errors?.find(e => e.propertyName === "FatherName")?.message}
            />
          </Col>
          <Col md={3}>
            <TextField
              label={t("common.schoolCandidateModel.motherName")}
              value={schoolCandidate.motherName}
              onChange={handleMotherNameChanged}
              errorText={errors?.find(e => e.propertyName === "MotherName")?.message}
            />
          </Col>
        </Row>
        {oibServiceResult &&
          <Row>
            {renderOibServiceInfo(true, 3, hrDate(oibServiceResult.item?.dateOfBirth ?? new Date), () => handleDateOfBirthChange(oibServiceResult.item!.dateOfBirth))}
            {renderOibServiceInfo(true, 3, genders?.find(v => v.id === oibServiceResult.item?.genderId)?.name, () => handleGenderChangeNumber(oibServiceResult.item!.genderId))}
            {renderOibServiceInfo(true, 3, oibServiceResult.item?.fatherName, () => handleFatherNameChangedString(oibServiceResult.item?.fatherName))}
            {renderOibServiceInfo(true, 3, oibServiceResult.item?.motherName, () => handleMotherNameChangedString(oibServiceResult.item?.motherName))}
          </Row>
        }
      </FormCard>

      <AddressSelect
        title={t("common.schoolCandidateModel.permanentAddress")}
        initialValue={schoolCandidate.residence?.permanentAddress}
        infoValue={oibServiceResult?.item?.residence?.permanentAddress}
        handleChange={(a) => handlePermanentAddressChange(a)}
        readOnly={false}
        disabled={schoolCandidate.isOibConnected}
        errorText={errors?.find(e => e.propertyName === "PermanentAddressId")?.message}
      >
        <Row>
          <Col md={12}>
            <TextField
              label={t("common.schoolCandidateModel.permanentAddressNote")}
              value={schoolCandidate.permanentAddressNote}
              onChange={handlePermanentAddressNoteChanged}
            />
          </Col>
        </Row>
      </AddressSelect>
      <AddressSelect
        title={t("common.schoolCandidateModel.temporaryAddress")}
        initialValue={schoolCandidate.residence?.temporaryAddress}
        handleChange={(a) => handleTemporaryAddressChange(a)}
        readOnly={false}
        disabled={schoolCandidate.isOibConnected}
        errorText={errors?.find(e => e.propertyName === "TemporaryAddressId")?.message}
      />

      <FormCard
        cardTitle={t("common.schoolCandidateModel.otherDetails")}
      >
        <Row>
          <Col md={6}>
            <Select
              label={t("common.schoolCandidateModel.schoolCandidateAddReason")}
              name={"schoolCandidate.schoolCandidateAddReason"}
              value={schoolCandidate.schoolCandidateAddReasonId}
              onChange={handleSchoolCandidateAddReasonChange}
              selectData={codeBookToSelectData(schoolCandidateAddReasons)}
              emptyOption={{ value: undefined, text: undefined }}
              errorText={errors?.find(e => e.propertyName === "AddReasonId")?.message}
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t("common.schoolCandidateModel.schoolCandidateAddReasonOther")}
              value={schoolCandidate.schoolCandidateAddReasonOther}
              onChange={handleSchoolCandidateAddReasonOtherChange}
              disabled={schoolCandidate.schoolCandidateAddReasonId !== 3}
              errorText={errors?.find(e => e.propertyName === "AddReasonOther")?.message}
            />
          </Col>
        </Row>
        {id !== "0" &&
        <Row>
          <Col md={6}>
            <Select
              label={t("common.schoolCandidateModel.schoolCandidateCancelReason")}
              name={"schoolCandidate.schoolCandidateCancelReason"}
              value={schoolCandidate.schoolCandidateCancelReasonId}
              onChange={handleSchoolCandidateCancelReasonChange}
              selectData={codeBookToSelectData(schoolCandidateCancelReasons)}
              emptyOption={{ value: undefined, text: undefined }}
              errorText={errors?.find(e => e.propertyName === "CancelReasonId")?.message}
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t("common.schoolCandidateModel.schoolCandidateCancelReasonOther")}
              value={schoolCandidate.schoolCandidateCancelReasonOther}
              onChange={handleSchoolCandidateCancelReasonOtherChange}
              disabled={schoolCandidate.schoolCandidateCancelReasonId !== 3}
              errorText={errors?.find(e => e.propertyName === "CancelReasonOther")?.message}
            />
          </Col>
        </Row>
        }
        <Row>
          <Col md={6}>
            <Select
              label={t("common.schoolCandidateModel.uoguCounty")}
              name={"schoolCandidate.uoguCounty"}
              required={true}
              value={schoolCandidate?.uoguCountyId}
              onChange={handleUOGUCountyChange}
              selectData={codeBookToSelectData(authCounties)}
              errorText={errors?.find(e => e.propertyName === "UoguCountyId")?.message}
              disabled={authCounties?.length === 1}
            />
          </Col>
          <Col md={6}>
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={"schoolCandidate.institution"}
              value={schoolCandidate.institutionId}
              onlyByEnrollmentArea={true}
              excludeHandicapped={true}
              excludePrivate={false}
              excludeSatellite={false}
              onChange={handleInstitutionChange}
              disabled={(Number(+id) !== 0 && schoolCandidate.statusId !== SchoolCandidateStatus.notSent)}
              countyId={schoolCandidate?.uoguCountyId}
              showUser={true} // false?
              errorText={errors?.find(e => e.propertyName === "InstitutionId")?.message}
            ></InstitutionSelect>
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Checkbox
              label={t("common.schoolCandidateModel.isHandicapped")}
              checked={schoolCandidate.isHandicapped}
              onChange={handleIsHandicappedChanged}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextField
              label={t("common.schoolCandidateModel.uoguNote")}
              value={schoolCandidate.uoguNote}
              onChange={handleUOGUNoteChange}
            />
          </Col>
        </Row>
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={"show"}
        onClickSave={handleSaveSchoolCandidate}
        setSaveErrors={setErrors}
      />
      <Row><Col md={12}>&nbsp;</Col></Row>
      <Row><Col md={12}>&nbsp;</Col></Row>
      <Row><Col md={12}>&nbsp;</Col></Row>
      <Row><Col md={12}>&nbsp;</Col></Row>
    </ContentArea>
  );
}

